import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { cacheDocOffline } from "@/lib/push";

export function formatBytes(n) {
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export async function downloadResourceFile(resourceId, file) {
  const path = `/resources/${resourceId}/files/${file.id}/download`;
  const res = await api.get(path, { responseType: "blob" });
  cacheDocOffline(`${process.env.REACT_APP_BACKEND_URL}/api${path}`);
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = file.original_filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export default function ResourceFileList({ resourceId, files }) {
  const [busy, setBusy] = useState(null);

  const download = async (f) => {
    setBusy(f.id);
    try { await downloadResourceFile(resourceId, f); }
    catch { toast.error("Download failed — please try again"); }
    finally { setBusy(null); }
  };

  if (!files?.length) {
    return <p className="text-xs text-muted-foreground italic" data-testid={`no-files-${resourceId}`}>Documents are being prepared — check back soon.</p>;
  }
  return (
    <>
    <ul className="space-y-1.5">
      {files.map((f) => (
        <li key={f.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`resource-file-row-${f.id}`}>
          <span className="flex items-center gap-2 min-w-0 flex-1 text-muted-foreground">
            <FileText className="w-4 h-4 shrink-0 text-primary" />
            <span className="truncate">{f.original_filename}</span>
            <span className="text-xs shrink-0">{formatBytes(f.size)}</span>
          </span>
          <Button size="sm" variant="outline" className="h-8 shrink-0" onClick={() => download(f)} disabled={busy === f.id}
            data-testid={`download-file-button-${f.id}`}>
            {busy === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Download className="w-3.5 h-3.5 mr-1" /> Download</>}
          </Button>
        </li>
      ))}
    </ul>
    <p className="text-[11px] text-muted-foreground mt-2" data-testid="offline-hint">Downloaded documents are saved for offline access in the installed app.</p>
    </>
  );
}
